import { Ability } from '@casl/ability';
import {
  useDeleteScheduleByExternalId,
  useGetTimeZones,
} from '@dispatch/shared/hooks';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  Actions,
  EditScheduleForm,
  editScheduleFormDefault,
  QueryResult,
  Schedule,
  Subjects,
} from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import {
  ConfirmModal,
  dateColumn,
  EMPTY_VALUE,
  Section,
  tableStyleOverrides,
  textColumn,
  useNotification,
} from '@dispatch/web/ui';
import { Stack } from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { getTimeWithOffset } from '@dispatch/shared';
import { useQueryClient } from 'react-query';

export type ResourceScheduleProps = {
  schedulesQuery?: QueryResult<Schedule[] | undefined, unknown>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  setEditScheduleForm: (editScheduleForm: EditScheduleForm) => void;
  setOpenEditSchedule: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  isReadOnly?: boolean;
};

export default function ResourceSchedule({
  schedulesQuery,
  permissions,
  setEditScheduleForm,
  setOpenEditSchedule,
  setIsEdit,
  isReadOnly = false,
}: ResourceScheduleProps) {
  const onClickEditSchedule = (editSchduleForm: EditScheduleForm) => {
    setIsEdit(true);
    setEditScheduleForm(editSchduleForm);
    setOpenEditSchedule(true);
  };

  const createScheduleForm = (
    startDateTime: Date,
    endDateTime: Date,
    externalId: string,
    timeZoneId: string,
    dailyClaimOwnerTasksCount: number
  ): EditScheduleForm => {
    return {
      ...editScheduleFormDefault,
      startDateTime: getTimeWithOffset(timeZoneId, startDateTime),
      endDateTime: getTimeWithOffset(timeZoneId, endDateTime),
      timeZoneId,
      externalId,
      dailyClaimOwnerTasksCount,
    };
  };
  const hasUpsertPermissions =
    permissions?.can?.('upsert', 'resource-schedules') || false;

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const { notifyError, notifySuccess } = useNotification();
  const [toDelete, setToDelete] = useState<string>('');

  const { tenantId } = useCurrentUser();
  const { timeZones } = useGetTimeZones(tenantId || '');

  const getTimezoneFromTimeZoneId = (timeZoneId: string) => {
    return (
      timeZones.find((item) => item.value === timeZoneId)?.title || 'unknown'
    );
  };

  const queryClient = useQueryClient();
  const onDeleteSchedule = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Success', 'Schedule was deleted');
      schedulesQuery?.refetch && schedulesQuery?.refetch();
      queryClient.invalidateQueries(['schedule-data']);
    } else {
      notifyError('Error', 'Error deleting schedule');
    }

    setOpenConfirmDeleteModal(false);
  };

  const { deleteSchedule } = useDeleteScheduleByExternalId(onDeleteSchedule);

  const onDeleteClick = (externalId: string) => {
    if (externalId) {
      setOpenConfirmDeleteModal(true);
      setToDelete(externalId);
    } else {
      notifyError('Error', 'Error deleting event');
    }
  };

  const renderConfirmDelete = () => {
    return (
      <ConfirmModal
        onConfirm={() => deleteSchedule(toDelete)}
        onDeny={() => setOpenConfirmDeleteModal(false)}
        confirmTitle={'Delete Schedule?'}
        confirmContent={
          'Deleting the selected schedule will remove availability for this Resource'
        }
        modalType={'delete'}
      />
    );
  };

  const columns = [
    dateColumn(
      {
        field: 'workingDayStartTime',
        headerName: 'Start Time',
        disableColumnMenu: true,
        valueGetter: (params) =>
          getTimeWithOffset(
            params?.row?.timeZoneId,
            new Date(params?.row?.workingDayStartTime)
          ) || EMPTY_VALUE,
      },
      'EEE MM/dd/yyyy p',
      'resource-schedule-row'
    ),
    dateColumn(
      {
        field: 'workingDayEndTime',
        sortable: false,
        disableColumnMenu: true,
        headerName: 'End Time',
        valueGetter: (params) =>
          getTimeWithOffset(
            params?.row?.timeZoneId,
            new Date(params?.row?.workingDayEndTime)
          ) || EMPTY_VALUE,
      },
      'EEE MM/dd/yyyy p'
    ),
    textColumn({
      field: 'timeZoneId',
      sortable: false,
      headerName: 'Timezone',
      disableColumnMenu: true,
      valueGetter: (params) =>
        getTimezoneFromTimeZoneId(params?.row?.timeZoneId),
    }),
    {
      field: 'actions',
      type: 'actions',
      disableColumnMenu: true,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="DELETE"
          onClick={() => onDeleteClick(params.row?.externalId)}
          disabled={isReadOnly}
        />,
      ],
    },
  ];

  const resourceSchedules = React.useMemo(() => {
    return (
      schedulesQuery?.data?.filter((queryData) => {
        return queryData?.isVisible;
      }) || []
    );
  }, [schedulesQuery?.data]);

  return (
    <Section>
      <div style={{ height: '500px' }} data-testid="resource-schedules-list">
        <DataGridPro
          disableVirtualization={isRunningInJest()}
          loading={schedulesQuery?.status === 'loading'}
          rows={resourceSchedules}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Schedules
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Schedules Found
              </Stack>
            ),
          }}
          columns={columns}
          sx={{ ...tableStyleOverrides }}
          getRowId={(row) => row.externalId}
          hideFooter={isRunningInJest()}
          hideFooterSelectedRowCount={true}
          disableSelectionOnClick={true}
          onRowClick={(value) => {
            hasUpsertPermissions &&
              !isReadOnly &&
              onClickEditSchedule(
                createScheduleForm(
                  value.row.workingDayStartTime,
                  value.row.workingDayEndTime,
                  value.row.externalId,
                  value.row.timeZoneId,
                  value?.row?.dailyClaimOwnerTasksCount || 0
                )
              );
          }}
          columnVisibilityModel={{
            actions: hasUpsertPermissions,
          }}
          pagination
          autoPageSize
        />
      </div>
      {openConfirmDeleteModal && renderConfirmDelete()}
    </Section>
  );
}
